/**
 * Linha do tempo de passos de um job, só apresentação (spec 2026-10-15 linha do tempo §4.3,
 * decisão D1). Quem chama normaliza os passos da própria feature e decide a ação de cada um;
 * aqui moram só a forma e as cores do docs/DESIGN_SYSTEM.md §2.3:
 * em curso = `inovareTeal` (nunca azul), concluído = `green`, falhou = `red`,
 * pendente e pulado = neutro. Sem barra colorida à esquerda e sem emoji.
 *
 * Adaptadores: `JobStepTimeline` (provisionamento de conta AWS) e a linha do tempo do
 * gateway WhatsApp.
 */
import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { ActionIcon, Badge, Box, Collapse, Code, Group, Stack, Text, ThemeIcon, Tooltip } from '@mantine/core'
import {
  IconAlertTriangle, IconCheck, IconChevronDown, IconChevronUp, IconCircleDashed,
  IconLoader2, IconMinus, IconRefresh, IconX,
} from '@tabler/icons-react'

export type EstadoDoPasso = 'concluido' | 'em_curso' | 'falhou' | 'pendente' | 'pulado'

export interface PassoDaLinha {
  chave: string
  rotulo: ReactNode
  status: EstadoDoPasso
  /** Texto curto antes do rótulo, em monospace (ex.: `#03`). */
  prefixo?: string
  icone?: ReactNode
  /** Contexto ao lado do rótulo, em cinza. */
  descricao?: ReactNode
  /** Linha de dados abaixo do rótulo (recurso, link). */
  dados?: ReactNode
  iniciado_em?: string | null
  concluido_em?: string | null
  /** Quantas vezes o passo rodou; o selo só aparece acima de 1. */
  tentativas?: number
  erro?: ReactNode
  /** Bloco extra que abre junto com o erro (ex.: id longo de recurso). */
  expansivel?: boolean
  testId?: string
}

export interface GrupoDaLinha {
  chave: string
  rotulo?: ReactNode
  /** Selos ao lado do rótulo do grupo. */
  extra?: ReactNode
  passos: PassoDaLinha[]
}

interface Props {
  grupos: GrupoDaLinha[]
  /** Ação por passo (ex.: "tentar de novo a partir daqui"); null não ocupa lugar. */
  acao?: (passo: PassoDaLinha) => ReactNode
  formatarDuracao: (segundos: number) => string
  /** Título do bloco de erro; a tela de contas AWS mantém o "Erro no step" de sempre. */
  rotuloDoErro?: string
  testId?: string
}

const MARCADOR: Record<EstadoDoPasso, { cor: string; Icone: typeof IconCheck; cheio: boolean; gira?: boolean; texto: string }> = {
  concluido: { cor: 'green', Icone: IconCheck, cheio: true, texto: 'Concluído' },
  em_curso: { cor: 'inovareTeal', Icone: IconLoader2, cheio: false, gira: true, texto: 'Em curso' },
  falhou: { cor: 'red', Icone: IconX, cheio: true, texto: 'Falhou' },
  pendente: { cor: 'gray', Icone: IconCircleDashed, cheio: false, texto: 'Pendente' },
  pulado: { cor: 'gray', Icone: IconMinus, cheio: false, texto: 'Pulado' },
}

export function LinhaDoTempoDePassos({ grupos, acao, formatarDuracao, rotuloDoErro = 'Erro no passo', testId }: Props) {
  return (
    <Stack gap="xl" data-testid={testId}>
      {grupos.map((g) => <Grupo key={g.chave} grupo={g} acao={acao} formatarDuracao={formatarDuracao} rotuloDoErro={rotuloDoErro} />)}
    </Stack>
  )
}

function Grupo({ grupo, acao, formatarDuracao, rotuloDoErro }: { grupo: GrupoDaLinha; acao?: Props['acao']; formatarDuracao: Props['formatarDuracao']; rotuloDoErro: string }) {
  const total = grupo.passos.length
  const feitos = grupo.passos.filter((p) => p.status === 'concluido').length
  return (
    <Box>
      {grupo.rotulo != null && (
        <Group gap="xs" mb="xs" wrap="nowrap" align="center">
          <Text size="xs" tt="uppercase" fw={700} style={{ letterSpacing: 1.2 }}>{grupo.rotulo}</Text>
          {grupo.extra}
          <Text size="xs" c="dimmed" ff="monospace">{feitos}/{total}</Text>
        </Group>
      )}
      <Box style={{ position: 'relative', paddingLeft: 12 }}>
        {/* A espinha liga os marcadores; é divisor neutro, não acento de estado. */}
        <Box style={{ position: 'absolute', left: 25, top: 14, bottom: 14, width: 1, background: 'var(--mantine-color-default-border)' }} />
        <Stack gap={2}>
          {grupo.passos.map((p) => <Linha key={p.chave} passo={p} acao={acao} formatarDuracao={formatarDuracao} rotuloDoErro={rotuloDoErro} />)}
        </Stack>
      </Box>
    </Box>
  )
}

function duracaoEmSegundos(inicio?: string | null, fim?: string | null): number | null {
  if (!inicio || !fim) return null
  const ms = new Date(fim).getTime() - new Date(inicio).getTime()
  return ms > 0 ? ms / 1000 : null
}

function Linha({ passo, acao, formatarDuracao, rotuloDoErro }: { passo: PassoDaLinha; acao?: Props['acao']; formatarDuracao: Props['formatarDuracao']; rotuloDoErro: string }) {
  const m = MARCADOR[passo.status]
  const falhou = passo.status === 'falhou'
  const [aberto, setAberto] = useState(falhou)
  useEffect(() => { if (falhou) setAberto(true) }, [falhou])
  const segundos = duracaoEmSegundos(passo.iniciado_em, passo.concluido_em)
  const comErro = falhou && passo.erro != null
  const expansivel = comErro || Boolean(passo.expansivel)
  const tentativas = passo.tentativas ?? 0
  const botao = acao?.(passo)

  return (
    <Box py="xs" data-testid={passo.testId} data-estado={passo.status}>
      <Group gap="md" wrap="nowrap" align="flex-start">
        <Box style={{ position: 'relative', zIndex: 1, flexShrink: 0 }}>
          <ThemeIcon
            color={m.cor} variant={m.cheio ? 'filled' : 'light'} radius="xl" size={28} aria-label={m.texto}
            style={{ border: '2px solid var(--mantine-color-body)', boxShadow: '0 0 0 1px var(--mantine-color-default-border)' }}
          >
            <m.Icone size={14} stroke={2.4} className={m.gira ? 'etapa-girando' : undefined} />
          </ThemeIcon>
        </Box>

        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
          <Group gap="xs" wrap="wrap" align="center">
            {passo.prefixo && <Text size="xs" c="dimmed" ff="monospace" fw={500} style={{ minWidth: 24 }}>{passo.prefixo}</Text>}
            <Group gap={6} wrap="nowrap">
              {passo.icone}
              <Text size="sm" fw={600}>{passo.rotulo}</Text>
            </Group>
            {passo.descricao && (
              <Text size="sm" c="dimmed" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>· {passo.descricao}</Text>
            )}
          </Group>

          <Group gap="xs" wrap="wrap">
            {passo.dados}
            {segundos != null && <Text size="xs" c="dimmed" ff="monospace">{formatarDuracao(segundos)}</Text>}
            {tentativas > 1 && (
              <Tooltip
                withArrow
                label={passo.status === 'concluido' ? `Recuperado na ${tentativas}ª tentativa`
                  : falhou ? `Falhando há ${tentativas} tentativas` : `${tentativas} tentativas`}
              >
                <Badge size="xs" variant="light" color={passo.status === 'concluido' ? 'yellow' : falhou ? 'red' : 'gray'}
                  leftSection={<IconRefresh size={9} />}>
                  {tentativas}×
                </Badge>
              </Tooltip>
            )}
          </Group>

          {comErro && (
            <Collapse in={aberto}>
              <Box mt="xs" p="sm" style={{ background: 'var(--mantine-color-red-light)', borderRadius: 6, border: '1px solid var(--mantine-color-red-light-hover)' }}>
                <Group gap="xs" mb={4} wrap="nowrap" align="flex-start">
                  <IconAlertTriangle size={14} color="var(--mantine-color-red-7)" style={{ marginTop: 2 }} />
                  <Text size="xs" c="red.8" fw={600}>{rotuloDoErro}</Text>
                </Group>
                {/* Texto cru (mensagem da AWS) vai como código; quem já traz a frase pronta
                    manda o nó e decide a tipografia. */}
                {typeof passo.erro === 'string'
                  ? <Code block style={{ fontSize: 11, whiteSpace: 'pre-wrap', background: 'transparent' }}>{passo.erro}</Code>
                  : passo.erro}
              </Box>
            </Collapse>
          )}
        </Stack>

        {(botao || expansivel) && (
          <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
            {botao}
            {expansivel && (
              <ActionIcon variant="subtle" color="gray" size="md" onClick={() => setAberto((v) => !v)} aria-label={aberto ? 'Recolher' : 'Expandir'}>
                {aberto ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
              </ActionIcon>
            )}
          </Group>
        )}
      </Group>
    </Box>
  )
}
